'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { createPortal } from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { SocialIcon, socialKind, SOCIAL_LABEL } from './SocialIcon'

type Item = { name: string; href: string }

export function MobileNav({
  items,
  siteName,
  social = [],
  email,
}: {
  items: Item[]
  siteName: string
  social?: string[]
  email?: string | null
}) {
  const pathname = usePathname()
  const socials = social.filter(Boolean)
  const [open, setOpen] = useState(false) // unica fonte di verità: aperto/chiuso, senza animazioni
  const [mounted, setMounted] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Portal disponibile solo dopo il mount (document esiste lato client).
  useEffect(() => {
    setMounted(true)
  }, [])

  // Chiudi al cambio di route: navigare (CTA "Cerca", link categoria, back del
  // browser) non deve mai lasciare il menu montato sopra la nuova pagina.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Scroll del body bloccato finché il menu è aperto.
  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  // Dialog accessibile: focus dentro il pannello all'apertura, ripristino al
  // trigger alla chiusura, Escape per chiudere e focus-trap sul Tab. Il trigger
  // (la X) vive nell'header fuori dal portal: lo includiamo nel ciclo del Tab
  // così resta raggiungibile da tastiera come controllo di chiusura.
  useEffect(() => {
    if (!open) return
    const focusables = () => {
      const els: HTMLElement[] = []
      if (triggerRef.current) els.push(triggerRef.current)
      if (panelRef.current) {
        els.push(
          ...Array.from(
            panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'),
          ),
        )
      }
      return els
    }
    panelRef.current?.querySelector<HTMLElement>('a[href]')?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
        return
      }
      if (e.key !== 'Tab') return
      const els = focusables()
      if (els.length === 0) return
      const first = els[0]
      const last = els[els.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      triggerRef.current?.focus()
    }
  }, [open])

  return (
    <div className="xl:hidden">
      {/* Unico toggle: burger quando chiuso, X quando aperto — resta nell'header. */}
      <button
        ref={triggerRef}
        type="button"
        aria-label={open ? 'Chiudi menu' : 'Apri menu'}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="grid size-10 place-items-center rounded-full text-zinc-700 transition-colors hover:bg-zinc-100"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        )}
      </button>

      {open && mounted
        ? createPortal(
            // Pannello sotto l'header (top-16), senza animazioni. z sotto l'header
            // (z-50) così l'header resta in alto e la X cliccabile.
            <div
              className="fixed inset-x-0 bottom-0 top-16 z-40"
              role="dialog"
              aria-modal="true"
              aria-label={`Menu di ${siteName}`}
            >
              <div ref={panelRef} className="absolute inset-0 flex flex-col bg-white p-6">
                <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
                  {items.map((it) => (
                    <Link
                      key={it.href}
                      href={it.href}
                      onClick={() => setOpen(false)}
                      className="rounded-lg px-3 py-2.5 text-base font-medium text-zinc-700 transition-colors hover:bg-accent-soft hover:text-accent-strong"
                    >
                      {it.name}
                    </Link>
                  ))}
                </nav>

                {/* Mini-footer: richiama il SiteFooter (social + contatto) per dare
                    un fondo coerente. Mostrato solo se c'è almeno un dato. */}
                {socials.length || email ? (
                  <div className="mt-6 shrink-0 border-t border-zinc-100 pt-6">
                    {socials.length ? (
                      <>
                        <p className="px-3 text-xs font-semibold uppercase tracking-widest text-zinc-600">Seguici</p>
                        <ul className="mt-3 flex flex-wrap gap-2 px-3">
                          {socials.map((url) => {
                            const kind = socialKind(url)
                            return (
                              <li key={url}>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="me noopener noreferrer"
                                  aria-label={SOCIAL_LABEL[kind]}
                                  className="grid size-10 place-items-center rounded-full border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-accent hover:bg-accent-soft hover:text-accent-strong"
                                >
                                  <SocialIcon kind={kind} />
                                </a>
                              </li>
                            )
                          })}
                        </ul>
                      </>
                    ) : null}
                    {email ? (
                      <a
                        href={`mailto:${email}`}
                        className="mt-4 flex items-center gap-2 px-3 text-sm font-medium text-zinc-600 transition-colors hover:text-accent-strong"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="m3 7 9 6 9-6" />
                        </svg>
                        <span className="break-all">{email}</span>
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
